import { useId, useState, type RefObject } from "react";
import { Tooltip, useFloatingPortal } from "@foris/avocado-suite";
import styles from "./annualScheduleComponentPill.module.scss";

interface Props {
  label: string;
  colorIndex: number;
}

/** Keeps component names compact while exposing their full text on hover or keyboard focus. */
export default function AnnualScheduleComponentPill({ label, colorIndex }: Props) {
  const tooltipId = useId();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const isOpen = (hovered || focused) && !dismissed;
  const { referenceRef, Portal } = useFloatingPortal({ isOpen, offset: [0, -8] });

  return (
    <>
      <button
        ref={referenceRef as RefObject<HTMLButtonElement>}
        type="button"
        className={`${styles.annualScheduleComponentPill} ${styles[`annualScheduleComponentPill__${colorIndex}`]}`}
        aria-describedby={isOpen ? tooltipId : undefined}
        onMouseEnter={() => {
          setHovered(true);
          setDismissed(false);
        }}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => {
          setFocused(true);
          setDismissed(false);
        }}
        onBlur={() => setFocused(false)}
        onClick={() => setDismissed(false)}
        onKeyDown={event => {
          if (event.key === "Escape") setDismissed(true);
        }}
      >
        {label}
      </button>
      <Portal>
        <Tooltip
          visible
          placement="top"
          label={
            <div
              id={tooltipId}
              role="tooltip"
              className={styles.annualScheduleComponentPill_tooltip}
            >
              {label}
            </div>
          }
        >
          <span className={styles.annualScheduleComponentPill_tooltipAnchor} />
        </Tooltip>
      </Portal>
    </>
  );
}
